/**
 * URL 参数同步的 React Hook
 * 提供与 useState 完全一致的 API，自动同步状态到 URL 查询参数
 * 
 * 注意：只支持状态到 URL 的单向同步，不支持 URL 变化时同步到状态
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, history } from 'umi';
import { debounce } from 'lodash-es';

/**
 * 类型定义
 */

/**
 * 支持的类型定义
 * URL 查询参数支持的基本类型和对象类型
 */
export type TSupportedValue = string | number | boolean | object | null | undefined;

/**
 * Hook 配置选项
 */
export interface IUseUrlParamOptions<T extends TSupportedValue = TSupportedValue> {
  /**
   * URL 参数名
   */
  urlKey: string;
  
  /**
   * 默认值（URL 中不存在时使用）
   */
  defaultValue: T;
  
  /**
   * 序列化函数：状态值 → URL 参数值
   * @param value - 状态值
   * @returns URL 参数值（字符串）
   */
  serialize?: (value: T) => string;
  
  /**
   * 反序列化函数：URL 参数值 → 状态值
   * @param value - URL 参数值（字符串）
   * @returns 状态值
   */
  deserialize?: (value: string) => T;
  
  /**
   * 防抖时间（毫秒），可选
   * 设置后，URL 更新会延迟执行，但状态更新立即生效
   */
  debounceMs?: number;
}

/**
 * Hook 返回值类型（与 useState 完全一致）
 */
export type TUseUrlParamReturn<T> = [
  T,
  (value: T | ((prev: T) => T)) => void
];

/**
 * 用于操作 URL 查询参数的 React Hook
 * 返回值与 useState 完全一致，可以直接替换 useState
 *
 * @param options - 配置选项
 * @returns [state, setState] - 与 useState 返回值格式完全一致
 *
 * @example
 * ```tsx
 * // 简单字符串类型
 * const [searchWords, setSearchWords] = useUrlParam({
 *   urlKey: 'searchWords',
 *   defaultValue: '',
 * });
 *
 * // Boolean 类型
 * const [isVisible, setIsVisible] = useUrlParam({
 *   urlKey: 'visible',
 *   defaultValue: false,
 * });
 *
 * // 复杂对象类型
 * const [filters, setFilters] = useUrlParam({
 *   urlKey: 'filters',
 *   defaultValue: { page: 1, size: 10 },
 * });
 *
 * // 带防抖
 * const [search, setSearch] = useUrlParam({
 *   urlKey: 'search',
 *   defaultValue: '',
 *   debounceMs: 300,
 * });
 * ```
 */
export const useUrlParam = <T extends TSupportedValue = TSupportedValue>(
  options: IUseUrlParamOptions<T>
): TUseUrlParamReturn<T> => {
  const {
    urlKey,
    defaultValue,
    serialize: userSerialize,
    deserialize: userDeserialize,
    debounceMs,
  } = options;

  const location = useLocation();
  const debouncedSyncRef = useRef<ReturnType<typeof debounce> | null>(null);

  // 简化的序列化函数
  // 注意：JSON.stringify 的局限性（当前场景不涉及，暂不处理）：
  // - undefined 在对象中会被忽略
  // - Date 对象会变成字符串
  // - 循环引用会抛出异常
  // - 函数、Symbol 会被忽略
  const serialize = useMemo(() => {
    if (userSerialize) {
      return userSerialize;
    }
    return (value: T): string => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'boolean') return String(value);
      return JSON.stringify(value);
    };
  }, [userSerialize]);

  // 简化的反序列化函数（仅用于初始化）
  // Boolean 类型处理：只有当 defaultValue 是 boolean 类型时，才将 'true'/'false' 转换为 boolean
  // 注意：不处理大对象和特殊值（NaN、Infinity），当前场景不涉及
  const deserialize = useMemo(() => {
    if (userDeserialize) {
      return userDeserialize;
    }
    const isBooleanDefault = typeof defaultValue === 'boolean';
    
    return (str: string): T => {
      if (!str) return defaultValue;
      
      // 只有当 defaultValue 是 boolean 类型时，才处理 'true'/'false'
      if (isBooleanDefault) {
        if (str === 'true') return true as T;
        if (str === 'false') return false as T;
      }
      
      try {
        return JSON.parse(str) as T;
      } catch {
        return defaultValue;
      }
    };
  }, [userDeserialize, defaultValue]);

  // 从 URL 读取初始值
  const [state, setStateInternal] = useState<T>(() => {
    const urlValue = location.query?.[urlKey];
    if (!urlValue) return defaultValue;
    
    try {
      // URL 解码：还原编码后的值
      const decodedValue = decodeURIComponent(String(urlValue));
      return deserialize(decodedValue);
    } catch {
      // 解码失败，使用默认值
      return defaultValue;
    }
  });

  // 同步到 URL 的函数
  const syncToUrl = useCallback(
    (value: T) => {
      let serializedValue: string;
      try {
        serializedValue = serialize(value);
      } catch (error) {
        console.warn(`Failed to serialize value for URL parameter "${urlKey}":`, error);
        return;
      }

      // URL 编码：防止特殊字符破坏 URL 结构
      const encodedValue = encodeURIComponent(serializedValue);

      const currentQuery = location.query || {};
      const newQuery = {
        ...currentQuery,
        [urlKey]: encodedValue,
      };

      history.replace({
        pathname: location.pathname,
        query: newQuery,
      });
    },
    [urlKey, serialize, location.query, location.pathname]
  );

  // 根据是否需要防抖创建不同的同步函数
  const performSync = useMemo(() => {
    const shouldDebounce = debounceMs && debounceMs > 0;

    if (shouldDebounce) {
      // 清理之前的防抖函数
      if (debouncedSyncRef.current) {
        debouncedSyncRef.current.cancel();
      }
      // 使用 lodash-es 的 debounce 创建防抖函数
      debouncedSyncRef.current = debounce(syncToUrl, debounceMs);
      return debouncedSyncRef.current;
    } else {
      // 清理防抖函数（如果存在）
      if (debouncedSyncRef.current) {
        debouncedSyncRef.current.cancel();
        debouncedSyncRef.current = null;
      }
      // 立即同步函数
      return syncToUrl;
    }
  }, [syncToUrl, debounceMs]);

  // 包装 setState，添加 URL 同步逻辑
  const setState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStateInternal((prev) => {
        const newValue = typeof value === 'function' 
          ? (value as (prev: T) => T)(prev)
          : value;
        
        // 自动同步到 URL
        performSync(newValue);
        
        return newValue;
      });
    },
    [performSync]
  );

  // 清理防抖函数
  useEffect(() => {
    return () => {
      if (debouncedSyncRef.current) {
        debouncedSyncRef.current.cancel();
        debouncedSyncRef.current = null;
      }
    };
  }, []);

  return [state, setState] as TUseUrlParamReturn<T>;
};

